import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { CreateItemAttachmentRequestDto } from '@mimir/common/dto/itemAttachmentDto';
import type { LimitedNodeApi } from '@mimir/common/nodejs/limitedNodeApi/nodeApi';
import { getBasenameWithoutExtension, getDirname } from '@mimir/common/platformAbstraction/pathComposition';
import type { Logger } from '@mimir/common/types/logger';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import { deleteFile, getLocalFileSize, isDirAvailableLocally } from '@mimir/common/util/fs';
import { isAxiosError } from '@mimir/common/util/httpError';
import { makeDefaultMzipOps } from '@mimir/mzip/defaultMzipOps';
import { uploadAttachmentToItem } from '../upload/uploadAttachmentToItem';
import type { BackendOps } from '../util/backendOperations';

/**
 * Get the name of the masks folder corresponding to the given project file. E.g., if the project file
 * is named "MyProject.prproj", the masks folder will be named "MyProject Masks".
 * https://helpx.adobe.com/premiere/desktop/add-video-effects/work-with-masks/object-masking.html
 *
 * @param projectOriginalFileName - The original project file name.
 * @returns The name of the masks folder.
 */
export const getMasksFolderName = (projectOriginalFileName: string) => `${getBasenameWithoutExtension(getBasenameWithoutExtension(projectOriginalFileName, '/'), '\\')} Masks`;
export const getMasksFolderZipFileName = (projectOriginalFileName: string) => `${getMasksFolderName(projectOriginalFileName)}.zip`;

export const getStatusCodeIfAxiosError = (error: unknown): number | null => isAxiosError(error) && error.response ? error.response.status : null;

export const getMaskAttachmentDownloadUrl = async (
  backendOps: BackendOps,
  itemId: string,
  masksAttachmentFileName: string,
) => {
  let url: string | null = null;
  try {
    url = await backendOps.getItemAttachmentDownloadUrl(itemId, masksAttachmentFileName);
  } catch (error) {
    const axiosErrorStatusCode = getStatusCodeIfAxiosError(error);
    if (axiosErrorStatusCode === 404) {
      // There is no mask file attachment. Just ignore and move on.
      return null;
    }

    throw error;
  }

  return url;
};

interface GetMasksFolderParams {
  applicationContext: AdobePanelContext | ElectronContext,
  logger: Logger,
  sourceProjectPath: string,
}
export const getMasksFolderPathToUpload = async (params: GetMasksFolderParams) => {
  const { applicationContext, logger, sourceProjectPath } = params;
  const sourceProjectDir = getDirname(sourceProjectPath, applicationContext.pathSep);
  const sourceObjectMasksDirectoryPath = `${sourceProjectDir}${applicationContext.pathSep}${getMasksFolderName(sourceProjectPath)}`;
  logger.info(`Checking for masks directory at ${sourceObjectMasksDirectoryPath}`);
  if (!await isDirAvailableLocally(applicationContext.nodeApi, logger, sourceObjectMasksDirectoryPath)) {
    return null;
  }
  return sourceObjectMasksDirectoryPath;
};

interface ZipMasksFolderParams {
  nodeApi: LimitedNodeApi,
  sourceObjectMasksDirectoryPath: string,
  outputZipFilePath: string,
}
export const zipMasksFolder = async (params: ZipMasksFolderParams) => {
  const { nodeApi, sourceObjectMasksDirectoryPath, outputZipFilePath } = params;
  const mzipOps = makeDefaultMzipOps();
  await mzipOps.createZipFromDirectory({
    nodeApi: nodeApi,
    sourceDirectoryPath: sourceObjectMasksDirectoryPath,
    outputZipFilePath,
  });
  return {
    masksZipFileSize: getLocalFileSize(nodeApi, outputZipFilePath),
  }
};

interface UploadMasksZipFileParams {
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    uploadManager: BasicUploadManager,
  },
  attachmentFileName: string,
  masksZipFilePath: string,
  itemId: string,
}
export const uploadMasksZipFileAsAttachment = async (params: UploadMasksZipFileParams) => {
  const { services, attachmentFileName, masksZipFilePath, itemId } = params;
  const sourceProjectBasename = getBasenameWithoutExtension(attachmentFileName, services.applicationContext.pathSep);

  const objectMasksAttachmentCreateRequest: CreateItemAttachmentRequestDto = {
    fileName: attachmentFileName,
    type: 'file',
    description: `Object Masks for Premiere project ${sourceProjectBasename}.prproj`,
  };
  await uploadAttachmentToItem(services, masksZipFilePath, itemId, objectMasksAttachmentCreateRequest, 'file');
};

interface UploadMasksFolderParams {
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    uploadManager: BasicUploadManager,
  },
  projectPath: string,
  attachmentFileName: string,
  tempDir: string,
  itemId: string,
}
export const uploadMasksFolderAsAttachment = async (params: UploadMasksFolderParams) => {
  const { services, attachmentFileName, tempDir, itemId, projectPath } = params;
  const sourceProjectPath = projectPath;
  const sourceObjectMasksDirectoryPath = await getMasksFolderPathToUpload({
    applicationContext: services.applicationContext,
    logger: services.logger,
    sourceProjectPath,
  });
  if (!sourceObjectMasksDirectoryPath) {
    return;
  }

  const outputZipFilePath = `${tempDir}${services.applicationContext.pathSep}${getMasksFolderZipFileName(itemId)}`;
  try {
    await zipMasksFolder({
      nodeApi: services.applicationContext.nodeApi,
      sourceObjectMasksDirectoryPath,
      outputZipFilePath,
    });

    await uploadMasksZipFileAsAttachment({
      services,
      attachmentFileName,
      masksZipFilePath: outputZipFilePath,
      itemId,
    });
  } finally {
    deleteFile(services.applicationContext.nodeApi, outputZipFilePath);
  }
};
